import { themeGet } from "@styled-system/theme-get";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import { PrizeItem } from "models";
dayjs.locale("ja");

export const getTheme = (query: string, fallback?: string) =>
  themeGet(query, fallback);

export const convertHexToRGB = (hex) => {
  // check if it's a rgba
  if (hex.match("rgba")) {
    let triplet = hex.slice(5).split(",").slice(0, -1).join(",");
    return triplet;
  }

  let c;

  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = "0x" + c.join("");

    return [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",");
  }
};

export const getSearchQueryObj = (key) => {
  const searchQuery = location.search;
  const queryObj = {};
  searchQuery
    .slice(1)
    .split("&")
    .map((str) => (queryObj[str.split("=")[0]] = str.split("=")[1]));
  return queryObj[key];
};

export const formatJapanDate = (date) => {
  return dayjs(date).format("YYYY年MM月DD日 (dd)");
};
export const formatNormalDate = (date) => {
  return dayjs(date).format("YYYY/MM/DD");
};

export const formatNormalDateHour = (date) => {
  return dayjs(date).format("YYYY/MM/DD HH:mm");
};

/**
 * Adds thousands separators to a number.
 *
 * @param n - The number to add separators to.
 * @returns The number with thousands separators added.
 */
export const addThousandsSeparators = (n: number) => {
  return ("" + n)
    .split("")
    .reverse()
    .join("")
    .match(/(\d{1,3})/g)
    .join(",")
    .split("")
    .reverse()
    .join("");
};

/**
 * Formats the title by removing all occurrences of '%' and replacing spaces with '+'.
 * @param {string} v - The input title.
 * @returns {string} - The formatted title.
 */
export const formatTitle = (v) => {
  const withoutPercentage = v.replace(/%/g, ""); // Remove all occurrences of '%'
  return withoutPercentage.split(" ").join("+");
};

export function isInDateRange(
  currentDate: Date,
  startAt: Date,
  endedAt: Date
): boolean {
  // Set time to 00:00:00 for startAt
  startAt.setHours(0, 0, 0, 0);

  // Set time to 23:59:59 for endedAt
  endedAt.setHours(23, 59, 59, 999);

  return currentDate >= startAt && currentDate <= endedAt;
}

/**
 * Cuts a string to a specified length and appends ellipsis if necessary.
 * @param str - The string to be cut.
 * @param length - The maximum length of the cut string.
 * @returns The cut string with ellipsis if necessary.
 */
export function cutString(str: string, length: number): string {
  if (!str) return "";
  const cut = str.slice(0, length);

  if (cut.length < str.length) {
    return cut + "...";
  } else {
    return cut;
  }
}

/**
 * Counts the number of mail prizes in the given array of PrizeItems.
 * @param prizes - The array of PrizeItems to count.
 * @returns The number of mail prizes.
 */
export function countMailPrizes(prizes: PrizeItem[]): number {
  return prizes.reduce((count, prize) => {
    if (prize.desired_prize_type === "MAIL") {
      return count + 1;
    }
    return count;
  }, 0);
}

/**
 * Formats the given points into a string representation.
 * If the points string length is greater than 4, it truncates the string and appends "..." at the end.
 * @param points - The points to be formatted.
 * @returns The formatted points as a string.
 */
export function formatPoints(points: number): string {
  const pointsString = points.toString();
  return pointsString.length > 4
    ? `${pointsString.slice(0, 4)}...`
    : pointsString;
}
